import React, { useMemo, useState } from 'react';
import Table from '../../../../theme/ui/Atoms/Table/Table';
import Button from '../../../../theme/ui/Atoms/Button';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AirConnectIcon, ReinstallIcon, TrashIcon } from '../../../../assets';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import * as yup from 'yup';
import useBack from '../../../../shared/util/route-dom/hooks/useBack';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../../../../theme/ui/Atoms/Input';
import InputSelect from '../../../../theme/ui/Atoms/InputSelect';
import BackButton from '../BackButton';
import CommanderAirConnectAPI from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI';
import {
  AirConnectNode,
  CommanderAirConnectCreateComposableDeviceItemAPI,
  ComposableDeviceControlUnitMap,
} from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.model';
import useParseErrorMessage from '../../../../shared/hooks/useParseErrorMessage';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import FlexeserveDeviceAPI from '../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import { DevicesStoreInfoResponse } from '../../../../service/store/StoreService.model';
import ZoneManagerConnected from '../ZoneManagerConnected';
import useMyWorkspace from '../../../../redux/workspace/hooks/useMyWorkspace';
import useMachineTypes from '../../../../redux/workspace/hooks/useMachineTypes';
import cleanObject from '../../../../shared/util/lodash/object/cleanObject';
import { isNotNullOrUndefined } from '../../../../shared/util/collection/isNotNullOrUndefined';
import { AIR_CONNECT_ZONE_DESCRIPTION_OPTIONS } from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.constants';
import { useDeviceNameOptions } from '../../../../redux/workspace/util';
import useDevicesWithComposableDevice, {
  ComposableDevicesStoreInfoResponse,
} from '../../../../service/store/hooks/useDevicesWithComposableDevice';
import { INSTALLATION_TYPE, isUpdating } from '../utils';
import GroupAPI, {
  useGetGroupByIdQuery,
} from '../../../../service/group/GroupAPI';
import {
  ComposableDevice,
  GroupWithComposableDevice,
} from '../../../../service/group/group.model';
import useMessage from '../../../../shared/hooks/useMessage';
import { ModalInfo } from '../../../../theme/ui/Atoms/Modal';
import { IconButton } from '../../../../theme/ui/Atoms/Button/Button.stories';
import { MachineType } from '../../../../redux/workspace/WorkspaceAPI.model';
import { findDuplicatedDevices } from '../../../../service/device/util/getDeviceMappings';
import Badge from '../../../../theme/ui/Atoms/Badge/Badge';
import { M_SECONDS_POOLING_SETUP } from '../../../../service/store/Store.constants';

// one array of at least one string
const validationSchema = yup
  .array()
  .of(
    yup.object({
      mac: yup.string().required(),
      name: yup
        .string()
        .required('initial_setup.device_name_required')
        .nullable(),
      composableDevice: yup.object({
        machineTypeId: yup.string().required(),
      }),
      serialnumber: yup.string().nullable(),
    })
  )
  .test(
    'unique',
    'initial_setup.scan_air_connect.serial_number_unique',
    (value, c) => {
      const serialNumbers = value?.filter((v) => v.serialnumber);

      const isUnique =
        new Set(serialNumbers?.map((v) => v.serialnumber)).size ===
        serialNumbers?.length;

      return isUnique
        ? true
        : c.createError({
            path: 'serialnumberError',
            message: 'initial_setup.scan_air_connect.serial_number_unique',
          });
    }
  )
  .test(
    'uniqueMac',
    'initial_setup.scan_air_connect.mac_address_unique',
    (value, c) => {
      const macAddresses = value?.filter((v) => v.mac);

      const isUnique =
        new Set(macAddresses?.map((v) => v.mac)).size === macAddresses?.length;

      return isUnique
        ? true
        : c.createError({
            path: 'macAddressError',
            message: 'initial_setup.scan_air_connect.mac_address_unique',
          });
    }
  );

const getDefaultMachineType = (machineTypes?: any[], nodeType?: string) => {
  return machineTypes?.find(
    (machineType) => machineType.labels.nodeType === nodeType
  );
};

const parseNodesToInitValues = (
  nodes?: AirConnectNode[],
  flexeserveDevice?: DevicesStoreInfoResponse,
  machineTypes?: MachineType[],
  flexeServeList: ComposableDevicesStoreInfoResponse[] = []
): CommanderAirConnectCreateComposableDeviceItemAPI[] | undefined => {
  if (!nodes || !flexeserveDevice) return undefined;

  return [
    ...flexeServeList.map((flexeServeDevice) => {
      const node = nodes.find(
        (node) => node.macAddress === flexeServeDevice.mac
      );

      const defaultMachineType = getDefaultMachineType(
        machineTypes,
        node?.nodeType.toString()
      );
      const addressRelated = Array.from(
        { length: node?.numberOfControllers || 0 },
        (_, i) => ((node?.nodeAddress ?? 0) + i).toString()
      );
      const controlUnitsRelated =
        flexeserveDevice?.shelfMappings
          ?.filter(isNotNullOrUndefined)
          .filter((shelfMapping) =>
            addressRelated.includes(shelfMapping.cuaddr)
          ) || [];
      const controlUnits: ComposableDeviceControlUnitMap[] =
        flexeServeDevice.composableDevice?.controlUnits ||
        controlUnitsRelated.map((controlUnit) => ({
          deviceId: flexeserveDevice?.commanderId,
          controlUnitId: controlUnit.cuid.toString(),
          controlUnitBusAddress: controlUnit.cuaddr,
        }));
      const zoneMapping: Record<string, string> =
        flexeServeDevice.composableZoneMapping ||
        controlUnitsRelated.reduce((acc, controlUnit, currentIndex) => {
          return {
            ...acc,
            [(currentIndex + 1).toString()]: controlUnit.cuaddr,
          };
        }, {});

      const labels = flexeServeDevice?.composableDevice?.labels || {};
      const extraLabels =
        flexeServeDevice?.composableDevice?.controlUnits.reduce(
          (acc, _index, currentIndex) => {
            return {
              ...acc,
              [`zone_${currentIndex}_description`]:
                labels[`zone_${currentIndex}_description`] ?? '',
            };
          },
          {}
        );
      return {
        mac: flexeServeDevice.mac || node?.macAddress || '',
        name: flexeServeDevice?.unitName ?? null,
        serialnumber: flexeServeDevice?.serialnumber ?? null,
        model: 'AIR_CONNECT',
        composableDevice: {
          machineTypeId:
            flexeServeDevice?.composableDevice?.machineTypeId ??
            defaultMachineType?.id,
          controlUnits,
          labels: {
            'node.bitmask':
              labels['node.bitmask'] ?? (node?.bitmask.toString() || ''),
            'node.nodeNumber':
              labels['node.nodeNumber'] ?? (node?.nodeNumber.toString() || ''),
            'node.nodeAddress':
              labels['node.nodeAddress'] ??
              (node?.nodeAddress.toString() || ''),
            'node.numberOfControllersInstalled':
              labels['node.numberOfControllersInstalled'] ??
              (node?.numberOfControllersInstalled.toString() || ''),
            'node.numberOfControllers':
              labels['node.numberOfControllers'] ??
              (node?.numberOfControllers.toString() || ''),
            'node.nodeType':
              labels['node.nodeType'] ?? (node?.nodeType.toString() || ''),
            'node.nodeReachable':
              labels['node.nodeReachable'] ??
              (node?.nodeReachable.toString() || ''),
            ...extraLabels,
          },
        },
        zoneMapping,
      };
    }),
    ...nodes
      .filter(
        (node) =>
          !flexeServeList.find(
            (flexeserveNode) => flexeserveNode.mac === node.macAddress
          )
      )
      .map((node) => {
        const defaultMachineType = getDefaultMachineType(
          machineTypes,
          node.nodeType.toString()
        );
        const addressRelated = Array.from(
          { length: node.numberOfControllers },
          (_, i) => (node.nodeAddress + i).toString()
        );
        const controlUnitsRelated =
          flexeserveDevice?.shelfMappings
            ?.filter(isNotNullOrUndefined)
            .filter((shelfMapping) =>
              addressRelated.includes(shelfMapping.cuaddr)
            ) || [];
        const controlUnits: ComposableDeviceControlUnitMap[] =
          controlUnitsRelated.map((controlUnit) => ({
            deviceId: flexeserveDevice?.commanderId,
            controlUnitId: controlUnit.cuid.toString(),
            controlUnitBusAddress: controlUnit.cuaddr,
          }));
        const zoneMapping: Record<string, string> = controlUnitsRelated.reduce(
          (acc, controlUnit, currentIndex) => {
            return {
              ...acc,
              [(currentIndex + 1).toString()]: controlUnit.cuaddr,
            };
          },
          {}
        );
        return {
          mac: node.macAddress,
          name: null,
          serialnumber: null,
          model: 'AIR_CONNECT',
          composableDevice: {
            machineTypeId: defaultMachineType?.id,
            controlUnits,
            labels: {
              'node.bitmask': node.bitmask.toString(),
              'node.nodeNumber': node.nodeNumber.toString(),
              'node.nodeAddress': node.nodeAddress.toString(),
              'node.numberOfControllersInstalled':
                node.numberOfControllersInstalled.toString(),
              'node.numberOfControllers': node.numberOfControllers.toString(),
              'node.nodeType': node.nodeType.toString(),
              'node.nodeReachable': node.nodeReachable.toString(),
            },
          },
          zoneMapping,
        };
      }),
  ];
};
const isNewNode = (
  value: CommanderAirConnectCreateComposableDeviceItemAPI | null,
  devices: ComposableDevicesStoreInfoResponse[] = []
) => {
  return devices.find((device) => device.mac === value?.mac) === undefined;
};

const getSortedList = (
  flexeServeList: ComposableDevicesStoreInfoResponse[]
) => {
  return [...flexeServeList]
    .filter((item) => item.visible)
    .sort((a, b) => (a?.mac ?? '').localeCompare(b?.mac ?? ''));
};

const getDevicesToUpdate = (
  values: CommanderAirConnectCreateComposableDeviceItemAPI[],
  flexeserveDevicesList: ComposableDevicesStoreInfoResponse[]
) => {
  return values
    .map((device) => {
      const oldFlexeserveDevice = flexeserveDevicesList.find(
        (flexeserveDevice) => device.mac === flexeserveDevice.mac
      );
      if (oldFlexeserveDevice) {
        const flexeserveDevice = {
          ...oldFlexeserveDevice,
          ...device,
          unitName: device.name,
          composableZoneMapping: device.zoneMapping,
          composableDevice: {
            ...device.composableDevice,
          } as ComposableDevice,
        } as Partial<ComposableDevicesStoreInfoResponse>;
        const newValue = values.find((value) => value.mac === device.mac);
        const composableDevice = {
          id: flexeserveDevice.composableDeviceId ?? '',
          data: {
            name: flexeserveDevice.unitName,
            composableDevice: {
              channels: [],
              ...(newValue?.composableDevice ?? ({} as ComposableDevice)),
            },
          } as Partial<GroupWithComposableDevice>,
        };

        return {
          flexeserveDevice,
          composableDevice,
        };
      }

      return null;
    })
    .filter(isNotNullOrUndefined);
};

function DevicesSetUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const parseError = useParseErrorMessage();
  const showErrorMessage = useErrorMessage();
  const showMessage = useMessage();
  const {
    marketId,
    commanderId = '',
    serialNumber = '',
    storeId = '',
    installationType = INSTALLATION_TYPE.store,
  } = useParams<{
    marketId?: string;
    commanderId: string;
    serialNumber: string;
    storeId: string;
    installationType: INSTALLATION_TYPE;
  }>();
  const unitNameOptions = useDeviceNameOptions(marketId);

  const [deviceToDelete, setDeviceToDelete] =
    useState<CommanderAirConnectCreateComposableDeviceItemAPI | null>(null);
  const [displayConfirmModal, setDisplayConfirmModal] =
    useState<boolean>(false);

  const updating = isUpdating(installationType);

  const {
    isFetching: isFetchingNodesInstalled,
    data: nodesInstalled,
    error: errorNodesInstalled,
  } = CommanderAirConnectAPI.useGetNodesInstalledQuery(commanderId, {
    refetchOnMountOrArgChange: true,
    skip: !commanderId,
  });

  const [
    registerComposableDevice,
    {
      isLoading: isRegisteringComposableDevice,
      error: errorRegisterComposableDevice,
    },
  ] = CommanderAirConnectAPI.useRegisterNewComposableDeviceMutation();

  const {
    data: myWorkspace,
    error: errorMyWorkspace,
    isFetching: isFetchingMyWorkspace,
  } = useMyWorkspace();

  const {
    data: flexeserveDevice,
    error: errorFlexeserveDevice,
    isFetching: isFetchingFlexeserveDevice,
  } = FlexeserveDeviceAPI.useGetDeviceByFlexeserveDeviceIdQuery(commanderId, {
    refetchOnMountOrArgChange: true,
    skip: !commanderId,
    pollingInterval: M_SECONDS_POOLING_SETUP,
  });

  const {
    data: flexeserveDevicesList = [],
    isFetching: isFetchingFlexeserveDevicesList,
    error: errorFetchingFlexeserveDevicesList,
  } = FlexeserveDeviceAPI.useGetStoreDevicesQuery(storeId, {
    skip: !updating,
  });

  const sortedFlexeserveDevices = useMemo(
    () => getSortedList(flexeserveDevicesList),
    [flexeserveDevicesList]
  );

  const { devices } = useDevicesWithComposableDevice(
    storeId,
    sortedFlexeserveDevices || []
  );

  const { data } = useGetGroupByIdQuery(storeId ?? '');

  const composableDevices = data?.childGroups;

  const [updateFlexeserveDevice] =
    FlexeserveDeviceAPI.useUpdateSetupDeviceMutation();

  const [
    deleteFlexeserveDevice,
    {
      isLoading: isDeletingFlexeserveDevice,
      error: errorDeletingFlexeserveDevice,
    },
  ] = FlexeserveDeviceAPI.useDeleteFlexeserveDeviceMutation();

  const [updateComposableDevice, { error: errorUpdatingGroup }] =
    GroupAPI.useUpdateGroupMutation();

  const {
    data: machineTypes,
    isLoading: isLoadingMachineTypes,
    error: errorMachineTypes,
  } = useMachineTypes(undefined);

  const [
    reinstallNode,
    { isLoading: isReinstallingNode, error: errorReinstallingNode },
  ] = CommanderAirConnectAPI.useReinstallNodeMutation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [removeNode, { isLoading: isRemovingNode, error: errorRemovingNode }] =
    CommanderAirConnectAPI.useRemoveNodeMutation();

  const currentInitialValues = useMemo(
    () =>
      parseNodesToInitValues(
        nodesInstalled,
        flexeserveDevice,
        machineTypes,
        devices
      ) ?? [],
    [nodesInstalled, flexeserveDevice, machineTypes, devices]
  );

  const createOrUpdateDevice = async (
    nodes: CommanderAirConnectCreateComposableDeviceItemAPI[]
  ) => {
    const newNodes = nodes.filter(
      (device) => isNewNode(device, devices) && device.mac
    );
    if (newNodes.length) {
      await registerComposableDevice({
        workspaceId: myWorkspace?.id ?? '',
        storeId,
        data: {
          commanderSerialNum: serialNumber,
          devices: cleanObject(newNodes),
          storeId,
        },
      });
    }

    const devicesToUpdate = getDevicesToUpdate(
      nodes,
      flexeserveDevicesList
    ).map(({ flexeserveDevice, composableDevice }) => ({
      updateFlexeserveDevice: updateFlexeserveDevice(flexeserveDevice),
      updateComposableDevice: updateComposableDevice(composableDevice),
    }));
    return Promise.allSettled(Object.values(devicesToUpdate));
  };

  const removeNodeInstance = (): Promise<null> => {
    const nodeToRemove = nodesInstalled?.find(
      (node) => node.macAddress === deviceToDelete?.mac
    );
    if (!nodeToRemove) {
      showErrorMessage(t('error.device_not_found'));
      return Promise.reject(t('error.device_not_found'));
    }
    return removeNode({ commanderId, node: nodeToRemove }).unwrap();
  };

  const isDuplicated = (
    deviceToDelete: CommanderAirConnectCreateComposableDeviceItemAPI | null
  ) => {
    const device = flexeserveDevicesList.find(
      (device) => device.mac === deviceToDelete?.mac
    );
    const itemDuplicated = duplicated.find(
      (duplicatedItem) => duplicatedItem.id === device?.uuid
    );
    return !!itemDuplicated;
  };

  const onDelete = () => {
    let promise: Promise<void | null> | undefined;
    const device = flexeserveDevicesList.find(
      (device) => device.mac === deviceToDelete?.mac
    );
    const newNode = isNewNode(deviceToDelete, devices);
    const itemDuplicated = duplicated.find(
      (duplicatedItem) => duplicatedItem.id === device?.uuid
    );
    if (itemDuplicated) {
      // Just remove the duplicated flexeserve device
      promise = deleteFlexeserveDevice(device?.uuid ?? '').unwrap();
    } else if (newNode) {
      promise = removeNodeInstance();
    } else {
      promise = removeNodeInstance().then(() => {
        return deleteFlexeserveDevice(device?.uuid ?? '').unwrap();
      });
    }

    promise
      ?.then(() => {
        showMessage(
          t('initial_setup.connect_commander.device_deleted_successfully'),
          { variant: 'success' }
        );
      })
      .catch((error) => {
        showErrorMessage(
          t('initial_setup.connect_commander.device_delete_error', {
            error: parseError(error),
          })
        );
      })
      .finally(() => {
        setDisplayConfirmModal(false);
        setDeviceToDelete(null);
      });
  };
  const formik = useFormik({
    initialValues: currentInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (!formik.dirty) {
        navigate('../devices-overview-manager');
        return Promise.resolve();
      }
      return createOrUpdateDevice(values)
        .then(() => navigate('../devices-overview-manager'))
        .catch(showErrorMessage);
    },
  });
  const createOrUpdateDeviceBack = useBack('/');
  const duplicated = useMemo(
    () =>
      findDuplicatedDevices(
        sortedFlexeserveDevices,
        composableDevices,
        nodesInstalled
      ),
    [sortedFlexeserveDevices, composableDevices, nodesInstalled]
  );

  const loading =
    isFetchingNodesInstalled ||
    isRegisteringComposableDevice ||
    isFetchingFlexeserveDevice ||
    isFetchingMyWorkspace ||
    isLoadingMachineTypes ||
    isReinstallingNode ||
    isFetchingFlexeserveDevicesList ||
    isRemovingNode ||
    isDeletingFlexeserveDevice ||
    devices.length === 0;

  const error =
    errorNodesInstalled ||
    errorRegisterComposableDevice ||
    errorFlexeserveDevice ||
    errorMyWorkspace ||
    errorMachineTypes ||
    errorReinstallingNode ||
    errorFetchingFlexeserveDevicesList ||
    errorRemovingNode ||
    errorDeletingFlexeserveDevice ||
    errorUpdatingGroup;

  return (
    <>
      <Container maxWidth={false} sx={{ width: '85vw', margin: 'auto' }}>
        <Box
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fill: theme.palette.text.primary,
          })}
        >
          <Stack direction={'row'} alignItems={'center'}>
            {loading ? <CircularProgress /> : <AirConnectIcon height={'2em'} />}
            <Typography
              variant="subtitle2"
              marginY={2}
              sx={{
                display: 'flex',
                marginLeft: 3,
                alignItems: 'center',
              }}
            >
              {t('initial_setup.scan_air_connect.node_found', {
                count: nodesInstalled?.length || 0,
              })}
              : {nodesInstalled?.length || 0}
            </Typography>
          </Stack>
        </Box>

        <Container
          maxWidth={false}
          sx={{ mt: 4, overflowX: 'auto', overflowY: 'hidden', width: '100%' }}
        >
          {!!error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {parseError(error)}
            </Alert>
          )}
          <Table
            sx={{
              '& th.header': {
                minWidth: 300,
                textAlign: 'left',
              },
            }}
          >
            <caption></caption>

            <thead style={{ border: 'none' }}>
              <tr>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.mac_address')}
                </Typography>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_name')}
                </Typography>

                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_type')}
                </Typography>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_description')}
                </Typography>
                <Typography
                  component={'th'}
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_action_test')}
                </Typography>
                <Typography
                  component={'th'}
                  color="primary"
                  variant="subtitle2"
                  sx={{
                    width: '130px',
                  }}
                >
                  {t('initial_setup.connect_commander.reinstall')}
                </Typography>
                {updating && (
                  <Typography
                    component={'th'}
                    color="primary"
                    variant="subtitle2"
                    sx={{
                      width: '130px',
                    }}
                  >
                    {t('initial_setup.connect_commander.delete')}
                  </Typography>
                )}
              </tr>
            </thead>
            <tbody>
              {formik.values?.map((device, i) => {
                const flexeServeDevice = sortedFlexeserveDevices[i];
                const itemDuplicated = duplicated.find(
                  (duplicatedItem) =>
                    duplicatedItem.id === flexeServeDevice?.uuid
                );
                return (
                  <Paper
                    component={'tr'}
                    key={device.mac + i}
                    elevation={undefined}
                  >
                    <td style={{ position: 'relative' }}>
                      {itemDuplicated && (
                        <Badge type={itemDuplicated.type}>
                          <Typography>{t(itemDuplicated.label)}</Typography>
                        </Badge>
                      )}
                      {device.mac && isNewNode(device, devices) && (
                        <Badge type="new">
                          <Typography>
                            {t('initial_setup.connect_commander.new_node')}
                          </Typography>
                        </Badge>
                      )}
                      <Typography variant="body1">{device.mac}</Typography>
                    </td>
                    <td colSpan={2} style={{ padding: '1em 0px' }}>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <InputSelect
                            select
                            id={`[${i}].name`}
                            name={`[${i}].name`}
                            options={unitNameOptions.map((name) => ({
                              value: name,
                              text: name,
                            }))}
                            placeholder={t(
                              'initial_setup.scan_air_connect.device_name'
                            )}
                            fullWidth
                            required
                            aria-required
                            value={formik.values[i].name}
                            onChange={(v: string) =>
                              formik.setFieldValue(`${i}.name`, v)
                            }
                            onBlur={formik.handleBlur}
                            error={
                              formik.errors[i]?.name &&
                              Boolean(formik.errors[i]?.name)
                            }
                            helperText={t(formik.errors[i]?.name ?? '')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <InputSelect
                            select
                            id={`[${i}].device_type`}
                            name={`[${i}].device_type`}
                            options={machineTypes?.map((machineType) => ({
                              value: machineType.id,
                              text: machineType.name,
                            }))}
                            disable={isLoadingMachineTypes}
                            placeholder={t(
                              'initial_setup.scan_air_connect.device_type'
                            )}
                            fullWidth
                            required
                            aria-required
                            value={
                              formik.values[i].composableDevice.machineTypeId
                            }
                            onChange={(v: string) =>
                              formik.setFieldValue(
                                `${i}.composableDevice.machineTypeId`,
                                v
                              )
                            }
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid textAlign={'left'} size={12}>
                          <Input
                            label={t(
                              'initial_setup.scan_air_connect.serial_number'
                            )}
                            placeholder={t(
                              'initial_setup.scan_air_connect.serial_number'
                            )}
                            fullWidth
                            id={`[${i}].serialnumber`}
                            name={`[${i}].serialnumber`}
                            value={formik.values[i].serialnumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </td>
                    <td>
                      <Stack
                        textAlign={'left'}
                        py={1}
                        spacing={2}
                        height={'100%'}
                        justifyContent={
                          device.composableDevice.controlUnits.length > 1
                            ? 'space-around'
                            : 'flex-start'
                        }
                      >
                        {device.composableDevice.controlUnits.map(
                          (controlUnit, j) => (
                            <InputSelect
                              sx={[
                                device.composableDevice.controlUnits.length > 1
                                  ? {
                                      marginTop: 0,
                                    }
                                  : {
                                      marginTop: '4px',
                                    },
                              ]}
                              key={controlUnit.controlUnitAddress + '-' + j}
                              select
                              id={`[${i}].composableDevice.labels.zone_${j}_description`}
                              name={`[${i}].composableDevice.labels.zone_${j}_description`}
                              options={AIR_CONNECT_ZONE_DESCRIPTION_OPTIONS?.map(
                                (description) => ({
                                  value: description,
                                  text: description,
                                })
                              )}
                              placeholder={t(
                                'initial_setup.scan_air_connect.placeholder_zone_description'
                              )}
                              value={getIn(
                                formik.values,
                                `[${i}].composableDevice.labels.zone_${j}_description`
                              )}
                              //   onChange={formik.handleChange}
                              onChange={(v: string) =>
                                formik.setFieldValue(
                                  `${i}.composableDevice.labels.zone_${j}_description`,
                                  v
                                )
                              }
                              onBlur={formik.handleBlur}
                            />
                          )
                        )}
                      </Stack>
                    </td>
                    <td>
                      <Stack
                        textAlign={'left'}
                        height={'100%'}
                        py={1}
                        spacing={1}
                        justifyContent={
                          device.composableDevice.controlUnits.length > 1
                            ? 'space-around'
                            : 'flex-start'
                        }
                      >
                        {device.composableDevice.controlUnits.map(
                          (controlUnit, j) => {
                            const zoneIndex = parseInt(
                              device.zoneMapping[j + 1]
                            );
                            const zone =
                              flexeserveDevice?.shelfSettings[zoneIndex];
                            if (!zone) return null;
                            return (
                              <ZoneManagerConnected
                                size="small"
                                key={`${controlUnit.controlUnitAddress}-${j}`}
                                item={{
                                  device: {
                                    ...flexeserveDevice,
                                    composableDevice: device.composableDevice,
                                  } as any,
                                  zone,
                                }}
                                zoneIndex={zoneIndex}
                                loadingState={isFetchingFlexeserveDevice}
                                sx={[
                                  device.composableDevice.controlUnits.length >
                                  1
                                    ? {
                                        marginTop: 0,
                                      }
                                    : {
                                        marginTop: '8px',
                                      },
                                ]}
                              />
                            );
                          }
                        )}
                      </Stack>
                    </td>
                    <td>
                      {
                        <Button
                          sx={{
                            padding: 3,
                            borderRadius: '1em',
                            height: '85px',
                            width: '85px',
                          }}
                          variant="contained"
                          onClick={() => {
                            reinstallNode({
                              commanderId,
                              nodeNumber: parseInt(
                                device.composableDevice.labels[
                                  'node.nodeNumber'
                                ]
                              ),
                              nodeType:
                                device.composableDevice.labels['node.nodeType'],
                            });
                          }}
                          loading={isReinstallingNode}
                        >
                          <ReinstallIcon height={'3em'} />
                        </Button>
                      }
                    </td>
                    <td>
                      {updating && (
                        <IconButton
                          sx={(theme) => ({
                            padding: 3,
                            borderRadius: '1em',
                            height: '85px',
                            width: '85px',
                            borderColor: theme.palette.error.main,
                            color: theme.palette.error.main,
                          })}
                          variant="outlined"
                          loading={isDeletingFlexeserveDevice}
                          disabled={isReinstallingNode}
                          onClick={() => {
                            setDeviceToDelete(device);
                            setDisplayConfirmModal(true);
                          }}
                        >
                          <TrashIcon height={'3em'} />
                        </IconButton>
                      )}
                    </td>
                  </Paper>
                );
              })}
            </tbody>
          </Table>
          {getIn(formik.errors, 'serialnumberError') && (
            <Alert severity="error" sx={{ my: 2 }}>
              {t(`${getIn(formik.errors, 'serialnumberError')}`)}
            </Alert>
          )}
          {getIn(formik.errors, 'macAddressError') && (
            <Alert severity="error" sx={{ my: 2 }}>
              {t(`${getIn(formik.errors, 'macAddressError')}`)}
            </Alert>
          )}
        </Container>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Grid container spacing={2} justifyContent={'space-between'} mt={2}>
            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
            >
              <BackButton onClick={createOrUpdateDeviceBack} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                rounded
                fullWidth
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                onClick={() => formik.handleSubmit()}
              >
                {t('button.next')}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Container>
      <ModalInfo
        PaperProps={{
          sx: {
            padding: '2rem 4rem',
            maxWidth: '70vw',
            width: '100%',
          },
        }}
        open={displayConfirmModal}
        description={
          <Typography
            variant="body1"
            component={'p'}
            p="2rem 0 1rem 0"
            align="center"
          >
            {Object.keys(deviceToDelete?.zoneMapping || {}).length === 0 ||
            isDuplicated(deviceToDelete)
              ? t('initial_setup.connect_commander.removing_node_message', {
                  device: deviceToDelete?.name ?? deviceToDelete?.mac,
                })
              : `${t('initial_setup.connect_commander.removing_node_message', {
                  device: deviceToDelete?.name ?? deviceToDelete?.mac,
                })} ${t(
                  'initial_setup.connect_commander.remove_node_and_remove_device'
                )}`}
          </Typography>
        }
        title={
          <Typography fontSize="1.5rem" align="center">
            {t('initial_setup.connect_commander.removing_node_title', {
              device:
                deviceToDelete && !isNewNode(deviceToDelete, devices)
                  ? deviceToDelete?.name
                  : deviceToDelete?.mac,
            })}
          </Typography>
        }
        buttonActions={
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-evenly"
            width={'100%'}
          >
            <Button
              autoFocus
              variant="outlined"
              rounded
              disabled={isDeletingFlexeserveDevice || isRemovingNode}
              onClick={() => {
                setDisplayConfirmModal(false);
                setDeviceToDelete(null);
              }}
              sx={{ flex: '1 1 0px' }}
            >
              {t('button.cancel')}
            </Button>

            <Button
              autoFocus
              variant="contained-gray"
              rounded
              disabled={isDeletingFlexeserveDevice || isRemovingNode}
              onClick={() => onDelete()}
              sx={{ flex: '1 1 0px' }}
              loading={isDeletingFlexeserveDevice || isRemovingNode}
            >
              {t('initial_setup.connect_commander.delete')}
            </Button>
          </Stack>
        }
      />
    </>
  );
}

export default DevicesSetUp;
